import { inject, Injectable, Signal, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Station } from '@vueling-ng/vy-cdk-searcher';
import { Chip } from '@vueling-ng/vy-ds/src/lib/components/globals/chip';
import { DropdownItem } from '@vueling-ng/vy-ds/src/lib/components/globals/forms/dropdown';
import { SidePanelService } from '@vueling-ng/vy-ds/src/lib/components/globals/side-panel';
import { StateManagerService } from 'app/shared/state/state-manager.service';
import { map, Observable, take } from 'rxjs';
import { FilterPanelComponent } from '../components/filter-panel/filter-panel.component';
import { Filters } from '../models/bookings.interface';
import {
  CancelledFilters,
  DefaultFilters,
  defaultOption,
  DefaultOptionValue,
  emptyFilters,
  FilterControl,
  FilterTextBox,
  FlownFilters,
  PendingFilters,
  QuickFilters,
  QuickFiltersValue,
  selectFilters
} from '../models/filters.model';

@Injectable({
  providedIn: 'root'
})
export class BookingListFilterService {
  private readonly _sidePanelService = inject(SidePanelService);
  private readonly _stateManagerService = inject(StateManagerService);
  private readonly _translateService = inject(TranslateService);

  private readonly currentFilters = signal<Filters>(emptyFilters);

  private _chosenQuickFilter = QuickFiltersValue.None;
  private readonly _chosenCreditQuickFilter = signal<QuickFilters>(QuickFiltersValue.None);

  public openFiltersSidePanel(): void {
    this._sidePanelService.open(FilterPanelComponent, {
      title: 'booking_list_component.filters.filter_button',
      customData: {
        currentFilters: this.currentFilters(),
        availableFilters: this.getAvailableFilters()
      }
    });
  }

  public loadFilters(): Observable<void> {
    return this._sidePanelService.sidePanelOutputEvent.pipe(
      map((data) => {
        const filters = data as Filters;

        this.removeDefaultSelectFilters(filters);
        if (!filters.status) {
          filters.status = this._chosenQuickFilter;
        }

        this.setFilters(filters);
      })
    );
  }

  public deleteFilters(): void {
    const filters = { ...emptyFilters };
    filters.status = this._chosenQuickFilter;
    this.setFilters(filters);
  }

  public deleteFilter(filterId: string): void {
    const filters = this.currentFilters();
    filters[filterId as keyof Filters] = undefined;
    filters.status = this._chosenQuickFilter;
    this.setFilters(filters);
  }

  public changeQuickFilter(quickFilter: QuickFilters): void {
    const filters = this.currentFilters();
    filters.status = quickFilter;
    this.setFilters(filters);
    this._chosenQuickFilter = quickFilter;
  }

  public changeCreditQuickFilter(quickFilter: QuickFilters): void {
    this._chosenCreditQuickFilter.set(quickFilter);
  }

  public getCreditQuickFilter(): Signal<QuickFilters> {
    return this._chosenCreditQuickFilter;
  }

  public getCurrentFilters(): Signal<Filters> {
    return this.currentFilters;
  }

  public getChipsFromFilters(filters: Filters): Chip[] {
    const filtersRecord: Record<string, string> = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== undefined)
    );

    const chips: Chip[] = [];

    Object.keys(filtersRecord).forEach((filterId) => {
      if (!filtersRecord[filterId].includes('QuickFilters') && filtersRecord[filterId] != QuickFiltersValue.None) {
        let text = filtersRecord[filterId];
        if (this.isSelectFilter(filterId as FilterControl)) {
          if (this.isDefaultOption(filtersRecord[filterId])) return;
          text = this.getLabel(filterId, filtersRecord[filterId]);
        }

        const chip: Chip = {
          text: text,
          id: filterId
        };

        chips.push(chip);
      }
    });

    return chips;
  }

  private setFilters(filters: Filters): void {
    this.currentFilters.set({ ...filters });
  }

  private isSelectFilter(filterId: FilterControl): boolean {
    return selectFilters.includes(filterId);
  }

  private isDefaultOption(option: string): boolean {
    return option === DefaultOptionValue.Default || option === DefaultOptionValue.DefaultChannelType;
  }

  private getLabel(filterId: string, value: string): string {
    return DefaultFilters.find((filter) => filter.filterName === filterId)!.options!.find(
      (option) => option.value === value
    )!.label;
  }

  private getAvailableFilters(): FilterTextBox[] {
    let availableFilters: FilterTextBox[] = [];
    switch (this._chosenQuickFilter) {
      case QuickFiltersValue.None:
        availableFilters = DefaultFilters;
        break;

      case QuickFiltersValue.CancelledQuickFilters:
        availableFilters = CancelledFilters;
        break;

      case QuickFiltersValue.FlownQuickFilters:
        availableFilters = FlownFilters;
        break;

      case QuickFiltersValue.PendingQuickFilters:
        availableFilters = PendingFilters;
        break;

      default:
        availableFilters = DefaultFilters;
    }

    if (this._chosenQuickFilter !== QuickFiltersValue.CancelledQuickFilters) {
      this.setStationOptionsToFilters(availableFilters);
    }

    availableFilters = this.translateOptionsLabels(availableFilters);

    return availableFilters;
  }

  private setStationOptionsToFilters(filters: FilterTextBox[]): void {
    const originFilterIndex = filters.findIndex((filter) => filter.filterName === 'departureCode');
    const destinationFilterIndex = filters.findIndex((filter) => filter.filterName === 'returnCode');
    this._stateManagerService.state$.pipe(take(1)).subscribe((state) => {
      const stationOptions = this.parseStations(state.stations);
      stationOptions.unshift(defaultOption);
      filters[originFilterIndex].options = stationOptions;
      filters[destinationFilterIndex].options = stationOptions;
    });
  }

  private parseStations(stations: Station[] | undefined): DropdownItem[] {
    if (!stations) return [];

    return stations.map((station) => {
      return { label: station.fullName, value: station.stationCode };
    });
  }

  private translateOptionsLabels(filters: FilterTextBox[]): FilterTextBox[] {
    return filters.map((filter) => ({
      ...filter,
      options: filter.options?.map(({ value, label }) => ({
        value,
        label: this._translateService.instant(label) as string
      }))
    }));
  }

  private removeDefaultSelectFilters(filters: Filters): void {
    selectFilters.forEach((filterKey) => {
      if (
        filters[filterKey] === DefaultOptionValue.Default ||
        filters[filterKey] === DefaultOptionValue.DefaultChannelType
      ) {
        filters[filterKey] = undefined;
      }
    });
  }
}
